import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import React from 'react';
import styled from 'styled-components';

import type { DubCoverage } from '@src/editor/dubbing/DubCoverageService';

/**
 * Warns that part of this cut will be silent, or that some of it still says
 * older words than the current translation. Publishing with gaps is allowed, so
 * this informs rather than blocks, and a fully covered cut shows nothing.
 *
 * Gaps are stated in seconds of kept picture rather than in lines: a line count
 * means nothing to someone who does not know how long a line is, and counting
 * only kept picture stops the parts the cut left out being blamed on the missing
 * dub. An undubbed stretch really is silent — the original voice is not restored
 * over it and the music bed does not fill it.
 *
 * PLAT-11592 — one line, not a paragraph per problem. The language menu header
 * this sits in already carries the language, the Voice control and the music
 * line, so silence and older words are stated together as two short facts
 * ("18 s not dubbed yet · 4 s dubbed before later edits") and the full
 * sentences move to the line's hover tooltip. They are still counted
 * separately because a cut can have either without the other, and each has
 * its own fix: silence is filled, older words are re-recorded.
 */
export interface DubCoverageNoticeProps {
  coverage: DubCoverage | null;
  /** Omitted ⇒ the notice states the problem without offering an action. */
  onFillGaps?: () => void;
  /**
   * Re-record the lines whose words were edited after they were dubbed. Kept
   * apart from `onFillGaps` because the two runs are opposites:
   * filling gaps is additive over lines with no audio, while this FORCES
   * re-synthesis of lines that already have some. Omitted ⇒ the notice states
   * the older words without offering to fix them.
   */
  onReDubStale?: () => void;
  busy?: boolean;
  /**
   * PLAT-11532 — why the fill action cannot run on this clip, when it cannot
   * (the clip is over the dubbing cap). While set the action is inert and the
   * sentence is its hover tooltip, so the person learns the reason before
   * pressing rather than from a failed run.
   */
  fillGapsDisabledNote?: string;
  /**
   * The language, in words. The sentence names it every time: "this clip has no
   * dub" without a language reads as a statement about the whole clip when it is
   * about the one language being published.
   */
  noDubLanguageLabel?: string;
  /**
   * Which voice the clip is set to, where the surface has one. Left out on
   * surfaces with no such control, and deliberately so on the publish panel: the
   * publish path itself ignores the voice position except for "both voices", so
   * suppressing on it there would hide a real warning.
   */
  audioMode?: 'original' | 'dubbed' | 'both';
  /**
   * What the sentences call the thing being measured. Defaults to "clip",
   * which is what the clip preview is looking at. Creator Studio passes
   * "reel": there the same sum covers the whole story timeline, and calling
   * that a clip would name something that is not on screen (PLAT-11555).
   */
  subjectNoun?: string;
  /**
   * react-tooltip id for the full sentences behind the short facts and for the
   * reason an inert action cannot be pressed. The host renders the <Tooltip>
   * (a paragraph style — the content is one or two sentences). Omitted ⇒ the
   * facts stand alone.
   */
  tooltipId?: string;
}

/**
 * Rounded up and never to zero: a gap that rounded to "0 seconds" would read as
 * no gap at all while the clip really does fall silent. Under a second reads as
 * "less than a second" rather than "1 second", which would overstate it.
 */
export function describeSeconds(seconds: number): string {
  if (seconds < 1) return 'Less than a second';
  const whole = Math.ceil(seconds);
  if (whole < 60) return `${whole} second${whole === 1 ? '' : 's'}`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  const minutePart = `${minutes} minute${minutes === 1 ? '' : 's'}`;
  if (rest === 0) return minutePart;
  return `${minutePart} ${rest} second${rest === 1 ? '' : 's'}`;
}

/** The same rounding as `describeSeconds`, in the short form the one-line
 * notice has room for: "<1 s", "18 s", "2 min", "2 min 5 s". */
export function describeSecondsShort(seconds: number): string {
  if (seconds < 1) return '<1 s';
  const whole = Math.ceil(seconds);
  if (whole < 60) return `${whole} s`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`;
}

export const DubCoverageNotice: React.FC<DubCoverageNoticeProps> = ({
  coverage,
  onFillGaps,
  onReDubStale,
  busy = false,
  fillGapsDisabledNote,
  noDubLanguageLabel,
  audioMode,
  subjectNoun = 'clip',
  tooltipId,
}) => {
  // The delivered file keeps the original recording, so neither figure below
  // describes it: both are about a dub that will not be used.
  if (audioMode === 'original') return null;

  // With no dub at all, nothing silences the original and the delivered file
  // carries it at full volume, so "N seconds will be silent" would be false.
  const nothingDubbed =
    !!coverage && coverage.inClip > 0 && coverage.dubbed === 0;

  const hasGap = !!coverage && !nothingDubbed && coverage.gaps.length > 0;
  const hasStale = !!coverage && !nothingDubbed && coverage.stale.length > 0;
  if (!coverage || (!nothingDubbed && !hasGap && !hasStale)) return null;

  // Inert rather than disabled so the reason can still show on hover; the
  // label itself says why while a run is out. Both actions are gated by the
  // same note: it is the surface refusing to dub at all, not one run.
  const actionInert = busy || !!fillGapsDisabledNote;
  const renderAction = (label: string, onPress: () => void) => (
    <button
      type="button"
      className="action"
      aria-disabled={actionInert ? true : undefined}
      data-tooltip-id={fillGapsDisabledNote ? tooltipId : undefined}
      data-tooltip-content={fillGapsDisabledNote}
      onClick={() => {
        if (actionInert) return;
        onPress();
      }}
    >
      {busy ? 'Dubbing…' : label}
    </button>
  );
  const action = onFillGaps && renderAction('Dub it', onFillGaps);
  const reDubAction = onReDubStale && renderAction('Re-dub', onReDubStale);

  if (nothingDubbed) {
    // Informative, not a fault: a file that keeps its original voice because
    // nobody dubbed this language is doing what it should.
    return (
      <Wrapper $tone="informative" role="status" data-testid="dub-none-notice">
        <span className="text">
          This {subjectNoun} has no
          {noDubLanguageLabel ? ` ${noDubLanguageLabel}` : ''} dub — the
          published file will keep the original voice.
        </span>
        {action}
      </Wrapper>
    );
  }

  const facts: string[] = [];
  const sentences: string[] = [];
  if (hasGap) {
    facts.push(`${describeSecondsShort(coverage.gapSeconds)} not dubbed yet`);
    sentences.push(
      `${describeSeconds(coverage.gapSeconds)} of this ${subjectNoun} will be silent — it has no dubbed voice.`,
    );
  }
  if (hasStale) {
    const one = coverage.stale.length === 1;
    facts.push(
      `${describeSecondsShort(coverage.staleSeconds)} dubbed before later edits`,
    );
    sentences.push(
      `${describeSeconds(coverage.staleSeconds)} of this ${subjectNoun} still says the older words — ${
        one ? 'a line was' : 'lines were'
      } edited after ${one ? 'it was' : 'they were'} dubbed.`,
    );
  }

  return (
    <Wrapper role="status" data-testid="dub-coverage-notice">
      <span
        className="text"
        data-tooltip-id={tooltipId}
        data-tooltip-content={tooltipId ? sentences.join(' ') : undefined}
      >
        <CircleExclammationIcon
          width="13"
          height="13"
          strokeColor="currentColor"
          strokeWidth={1.75}
        />
        {facts.join(' · ')}
      </span>
      {/* Each action rides its own fact, because neither run fixes the other's
          problem: filling a gap leaves the older words in place, and
          re-recording an edited line adds no audio where there is none. A cut
          with both problems therefore offers both, in the order the facts are
          stated. */}
      <span className="actions">
        {hasGap && action}
        {hasStale && reDubAction}
      </span>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $tone?: 'fault' | 'informative' }>`
  /* Constant width, matching the music line above, so the max-content dropdown
     does not jump when this appears. */
  min-width: 340px;
  box-sizing: border-box;
  padding: 2px 8px 8px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  .text {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    /* The fault line is short by construction and stays on one line; the
       informative sentence may wrap. */
    white-space: ${({ $tone }) =>
      $tone === 'informative' ? 'normal' : 'nowrap'};
    font-size: var(--text-xs);
    /* Gold (--accent) reads as "worth reading, not a fault"; red stays for a
       delivered file that comes out wrong. */
    color: ${({ $tone }) =>
      $tone === 'informative'
        ? 'var(--accent)'
        : 'var(--destructive, #c0392b)'};
  }

  .text svg {
    flex: none;
  }

  /* One flex child however many actions there are, so space-between keeps
     splitting the row into facts then actions rather than spreading two
     buttons across it. Empty on a notice that offers none — it has no size
     of its own, so it cannot pull the facts off the left edge. */
  .actions {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .action {
    flex: none;
    cursor: pointer;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm, 4px);
    background: transparent;
    color: var(--foreground);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
    padding: 2px 8px;
  }

  .action[aria-disabled='true'] {
    cursor: default;
    color: var(--text-muted);
  }
`;
